// Import React & core libraries
import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'

// Import necessary styles
import './ConsentModal.css'

/**
 * ConsentModal
 *
 * A reusable, brand-styled modal/dialog for Halisi. Renders through a portal
 * so it always escapes parent stacking contexts (flex containers,
 * overflow:hidden, z-index trees) — same approach as AlertMessage.
 *
 * The modal box has no fixed height: it grows with whatever you put inside
 * it (a one-line message, a list of checkbox items, custom children) up to
 * a max-height, then the body scrolls internally. That's what makes it
 * reusable for both a quick confirm dialog and a longer consent form.
 *
 * ── Usage 1: simple confirm/alert (no checkboxes) ──────────────────────
 * <HalisiModal
 *   isOpen={isOpen}
 *   title="Delete routine?"
 *   message="This can't be undone."
 *   onConfirm={handleDelete}
 *   onCancel={() => setIsOpen(false)}
 * />
 *
 * ── Usage 2: consent / checkbox-gated modal ────────────────────────────
 * <HalisiModal
 *   isOpen={isOpen}
 *   title="Before you continue"
 *   message="Please review and accept the following:"
 *   items={[
 *     { id: 'terms', label: 'I agree to the Terms of Service', required: true },
 *     { id: 'privacy', label: 'I agree to the Privacy Policy', required: true },
 *     { id: 'updates', label: 'Send me product updates' }, // optional — not required
 *   ]}
 *   confirmButtonText="Continue"
 *   onConfirm={(checkedState) => handleAccept(checkedState)}
 *   onCancel={() => setIsOpen(false)}
 * >
 *   {/* optional content rendered between the message and the checkboxes —
 *       an explanatory list, a table, anything * /}
 *   <ul>
 *     <li>We delete the photo itself straight after the analysis.</li>
 *     <li>We keep only a plain outline of your hair.</li>
 *   </ul>
 * </HalisiModal>
 *
 * Props:
 * - isOpen (bool): whether the modal is rendered.
 * - title (string, optional): header text. Header (and its close button) is
 *   omitted entirely if not provided.
 * - message (string | node, optional): main body text, right under the title.
 * - subtext (string | node, optional): smaller supporting text under the message.
 * - children (node, optional): rendered between subtext and the checkboxes —
 *   use it for an explanatory list, a diagram, anything that needs to be read
 *   before the consent line(s).
 * - items (array, optional): checkbox rows — [{ id, label, required, defaultChecked }].
 *   Confirm is disabled until every item with required: true is checked.
 * - footnote (string | node, optional): small print rendered under the
 *   checkboxes — e.g. "You can withdraw this at any time in Settings."
 * - onConfirm (function): called with the checkedState map, e.g. { terms: true, privacy: true }.
 * - onCancel (function, optional): if provided, shows a Cancel button and enables
 *   overlay-click / Escape to close. Omit it for a single-button "OK" alert.
 * - confirmButtonText / cancelButtonText (string, optional): override button labels.
 * - closeOnOverlayClick (bool, default true): click outside the box to cancel.
 * - size ('sm' | 'md' | 'lg', default 'md'): max-width preset; height always
 *   follows content.
 */
const ConsentModal = ({
  isOpen,
  title,
  message,
  subtext,
  children,
  items = [],
  footnote,
  onConfirm,
  onCancel,
  confirmButtonText,
  cancelButtonText,
  onCheckedChange,
  closeOnOverlayClick = true,
  size = 'md',
}) => {
  const language = useSelector((state) => state?.language)
  const translations = useSelector((state) => state?.translationsData)

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language ? language.toUpperCase() : undefined,
  ) => {
    if (!data || !lang) return undefined
    const keys = keyPath.split('.')
    let result = data
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (Array.isArray(result)) {
        const found = result.find((item) => item.label === key)
        if (!found) return undefined
        result = found
      } else if (result[key] === undefined) {
        return undefined
      } else {
        result = result[key]
      }
    }
    if (
      typeof result === 'object' &&
      !Array.isArray(result) &&
      result[lang] !== undefined
    ) {
      return result[lang]
    }
    return result
  }

  const confirmLabel = confirmButtonText || 'Accept'
  const cancelLabel = cancelButtonText || 'Decline'

  // ── Checkbox state, keyed by item id — resets whenever the modal opens ─
  const initialChecked = useMemo(
    () =>
      Object.fromEntries(items.map((item) => [item.id, !!item.defaultChecked])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  )
  const [checkedState, setCheckedState] = useState(initialChecked)

  useEffect(() => {
    setCheckedState(initialChecked)
  }, [initialChecked])

  const toggleItem = (id) => {
    setCheckedState((prev) => {
      const next = {
        ...prev,
        [id]: !prev[id],
      }

      onCheckedChange?.(next)

      return next
    })
  }

  const requiredItems = items.filter((item) => item.required)
  const allRequiredChecked = requiredItems.every(
    (item) => checkedState[item.id],
  )

  // ── Close on Escape ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !onCancel) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const hasItems = items.length > 0
  const showCancelButton = !!onCancel && hasItems

  return ReactDOM.createPortal(
    <div
      className='halisi-modal-overlay'
      onClick={() => {
        if (closeOnOverlayClick && onCancel) onCancel()
      }}
    >
      <div
        className={`halisi-modal-box halisi-modal-${size}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? 'halisi-modal-title' : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className='halisi-modal-header'>
            <h2 id='halisi-modal-title' className='halisi-modal-title'>
              {title}
            </h2>
            {onCancel && (
              <button
                type='button'
                className='halisi-modal-close'
                aria-label='Close'
                onClick={onCancel}
              >
                &times;
              </button>
            )}
          </div>
        )}

        {/* Body — grows with content, scrolls internally past max-height */}
        <div className='halisi-modal-body'>
          {message && <p className='halisi-modal-message'>{message}</p>}
          {subtext && <p className='halisi-modal-subtext'>{subtext}</p>}

          {children && (
            <div className='halisi-modal-custom-content'>{children}</div>
          )}

          {hasItems && (
            <div className='halisi-modal-items'>
              {items.map((item) => (
                <label key={item.id} className='halisi-modal-checkbox-row'>
                  <input
                    type='checkbox'
                    checked={!!checkedState[item.id]}
                    onChange={() => toggleItem(item.id)}
                  />
                  <span
                    className='halisi-modal-checkbox-custom'
                    aria-hidden='true'
                  />
                  <span className='halisi-modal-checkbox-label'>
                    {item.label}
                    {item.required && (
                      <span className='halisi-modal-required'> *</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}

          {footnote && <p className='halisi-modal-footnote'>{footnote}</p>}
        </div>

        {/* Footer */}
        <div className='halisi-modal-divider' />
        <div className='halisi-modal-buttons'>
          {showCancelButton && (
            <button
              type='button'
              className='halisi-modal-button cancel'
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type='button'
            className='halisi-modal-button confirm'
            disabled={hasItems && !allRequiredChecked}
            onClick={() => onConfirm && onConfirm(checkedState)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ConsentModal
